/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import React from "react";
import kebabCase from "lodash/kebabCase";
import { t } from "i18n";
import { STORY_EDITOR_NEW_PATH } from "pages/story-editor/routes";
import { COLLECTIONS_CONTENT_SETTINGS_NEW_PATH } from "pages/collections/routes";
import { NEW_BREAKING_NEWS } from "pages/breaking-news/routes";
import { USERS_NEW_PATH } from "pages/settings/pages/users-and-roles/users/routes";
import { PUSH_NOTIFICATION_NEW_PATH } from "pages/content/push-notification/routes";
import { FORMS_EDITOR_PATH_EDIT } from "pages/forms/routes";
import { generatePath } from "react-router";
import { ContextMenuList, ContextMenuItem } from "components/context-menu/context-menu";
import WithGAEvent from "helpers/ga-analytics/with-ga-event";
import styles from "./add-new-menu.module.css";

interface ComponentProps {
  children: React.ReactNode;
}

interface AddNewMenuProps {
  canCreateBreakingNews?: boolean;
  canCreateCollections?: boolean;
  canCreateStory?: boolean;
  canAddUser?: boolean;
  canCreatePushNotifications?: boolean;
  canCreateForms?: boolean;
  Component: React.ComponentType<ComponentProps>;
}

const addNewItem = (label: string, openWindow: string, canShow?: boolean) => {
  return (
    <WithGAEvent label={`header-add-new-${kebabCase(label)}`}>
      {canShow && <ContextMenuItem label={t(label)} onClick={() => window.open(openWindow)} />}
    </WithGAEvent>
  );
};

const AddNewMenu: React.SFC<AddNewMenuProps> = ({
  canCreateStory,
  canCreateBreakingNews,
  canCreatePushNotifications,
  canCreateCollections,
  canAddUser,
  canCreateForms,
  Component
}) => {
  return (
    <Component>
      <div id="add-new" className={styles["add-new-menu"]} data-test-id="add-new-menu-list">
        <ContextMenuList>
          {addNewItem("story", STORY_EDITOR_NEW_PATH, canCreateStory)}
          {addNewItem("breaking_news", NEW_BREAKING_NEWS, canCreateBreakingNews)}
          {addNewItem("push_notification", PUSH_NOTIFICATION_NEW_PATH, canCreatePushNotifications)}
          {addNewItem("collection", COLLECTIONS_CONTENT_SETTINGS_NEW_PATH, canCreateCollections)}
          {addNewItem("user", USERS_NEW_PATH, canAddUser)}
          {addNewItem("form", generatePath(FORMS_EDITOR_PATH_EDIT, { formId: "new" }), canCreateForms)}
        </ContextMenuList>
      </div>
    </Component>
  );
};

export default AddNewMenu;
